import { useState, useCallback } from 'react';
import { usePoints } from '../context/PointsContext';
import { JourneyPointsService } from '../services/journeyPointsService';
import { JourneyApiService } from '../services/journeyApiService';
import { StepAction, JourneyStep } from '../types/journey';

interface UseJourneyPointsReturn {
  // Points checking
  checkActionAffordability: (pointsCost: number) => Promise<{
    canAfford: boolean;
    currentPoints: number;
    shortfall?: number;
    suggestions?: any;
  }>;
  
  // Action processing
  processActionCost: (actionType: string, pointsCost: number, stepTitle: string) => Promise<{
    success: boolean;
    error?: any;
  }>;
  
  // Points calculations
  calculateNetPoints: (action: StepAction) => number;
  calculateStepPoints: (step: JourneyStep) => {
    totalCost: number;
    totalReward: number;
    netPoints: number;
    completionBonus: number;
  };
  
  // Journey summary
  getJourneyPointsSummary: (journeyId: string) => Promise<{
    summary: any;
    milestones: any;
    userPoints: number;
  }>;
  
  // Earning suggestions
  getEarningSuggestions: (shortfall: number) => {
    suggestions: Array<{
      activity: string;
      points: number;
      description: string;
      timeEstimate: string;
    }>;
    quickestPath: string;
  };
  
  // Loading states
  loading: boolean;
  error: string | null;
}

export const useJourneyPoints = (): UseJourneyPointsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userPoints, refreshPoints } = usePoints();

  const checkActionAffordability = useCallback(async (pointsCost: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use current points from context if available, otherwise fetch fresh
      let currentPoints = userPoints;
      if (currentPoints === 0) {
        await refreshPoints();
        currentPoints = userPoints;
      }

      const sufficient = currentPoints >= pointsCost;
      const shortfall = sufficient ? undefined : pointsCost - currentPoints;
      
      let suggestions;
      if (shortfall) {
        suggestions = JourneyPointsService.getEarningSuggestions(shortfall);
      }

      return {
        canAfford: sufficient,
        currentPoints,
        shortfall,
        suggestions
      };
    } catch (err: any) {
      setError(err.message || 'Failed to check affordability');
      return {
        canAfford: false,
        currentPoints: 0,
        shortfall: pointsCost
      };
    } finally {
      setLoading(false);
    }
  }, [userPoints, refreshPoints]);

  const processActionCost = useCallback(async (actionType: string, pointsCost: number, stepTitle: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await JourneyApiService.processActionCost(
        '', // userId will be determined from auth context in the service
        actionType,
        pointsCost,
        stepTitle
      );

      if (result.success) {
        // Refresh points context after successful deduction
        await refreshPoints();
      }

      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to process action cost');
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, [refreshPoints]);

  const calculateNetPoints = useCallback((action: StepAction) => {
    return JourneyPointsService.calculateNetPoints(action);
  }, []);

  const calculateStepPoints = useCallback((step: JourneyStep) => {
    return JourneyPointsService.calculateStepTotalPoints(step, true);
  }, []);

  const getJourneyPointsSummary = useCallback(async (journeyId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      return await JourneyApiService.getJourneyPointsSummary(journeyId);
    } catch (err: any) {
      setError(err.message || 'Failed to get journey points summary');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEarningSuggestions = useCallback((shortfall: number) => {
    return JourneyPointsService.getEarningSuggestions(shortfall);
  }, []);

  return {
    checkActionAffordability,
    processActionCost,
    calculateNetPoints,
    calculateStepPoints,
    getJourneyPointsSummary,
    getEarningSuggestions,
    loading,
    error
  };
};